import { Box, Flex, Icon, SimpleGrid, Text, useColorModeValue } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { headingFont, typography } from 'theme/tokens';

interface FormSectionProps {
	/** Optional section label (e.g. "Pricing", "Schedule") */
	label?: string;
	/** Optional icon displayed before the label */
	icon?: React.ElementType;
	/** Number of columns for child fields */
	columns?: { base?: number; md?: number };
	children: ReactNode;
}

/**
 * Groups form fields under an optional label with icon and extending divider line.
 * Uses SimpleGrid for multi-column layouts.
 */
export default function FormSection({
	label,
	icon: IconComponent,
	columns = { base: 1, md: 1 },
	children,
}: FormSectionProps) {
	const labelColor = useColorModeValue('secondaryGray.600', 'gray.400');
	const iconColor = useColorModeValue('brand.500', 'brand.400');
	const dividerColor = useColorModeValue('gray.200', 'whiteAlpha.100');

	return (
		<Box>
			{label && (
				<Flex align="center" gap="10px" mb="16px" mt="8px">
					{IconComponent && (
						<Icon as={IconComponent} color={iconColor} boxSize="16px" flexShrink={0} />
					)}
					<Text
						{...typography.formSectionLabel}
						color={labelColor}
						fontFamily={headingFont}
						whiteSpace="nowrap"
					>
						{label}
					</Text>
					<Box flex={1} h="1px" bg={dividerColor} />
				</Flex>
			)}
			<SimpleGrid columns={columns} gap="16px">
				{children}
			</SimpleGrid>
		</Box>
	);
}
